<template>
    <div v-click-outside="hideFloat">
        <div
            class="d-flex flex-row w-100 align-items-center"
            role="button"
            data-test-role="search-filter-toggle"
            v-on:click="toggleFloat">
            <div class="text-truncate">
                {{ filterDisplayText }}
            </div>
            <div
                v-if="showPlusNumber"
                class="ms-1 text-bg-primary-200 badge rounded-pill">
                {{ plusNumber }}
            </div>
            <div class="ms-2 flex-grow-1 d-inline-flex justify-content-end align-items-center dropdown-toggle" />
        </div>
        <div
            v-if="showDropdown"
            class="float-selector w-100 p-2 max-vh-75 bg-white border border-gray-600">
            <div class="options-container pe-2">
                <div v-if="ungroupedOptions.length > 0">
                    <pc-checkbox
                        v-for="(option, i) in ungroupedOptions"
                        v-bind:key="i"
                        v-bind:label="option[filterProps.textProp]"
                        v-bind:model-value="filterIsSelected(option)"
                        v-bind:data-test-role="`filter-${option[filterProps.idProp]}`"
                        v-on:update:model-value="updateFilterSelected(option)" />
                    <hr class="my-2" />
                </div>
                <div
                    v-for="(group, i) in groupedOptions"
                    v-bind:key="`group_${i}`">
                    <hr
                        v-if="i > 0"
                        class="my-2" />
                    <span class="fw-bold">
                        {{ group.name }}
                    </span>
                    <pc-checkbox
                        v-for="(option, j) in group.options"
                        v-bind:key="`group_${i}_${j}`"
                        v-bind:label="option[filterProps.textProp]"
                        v-bind:model-value="filterIsSelected(option)"
                        v-bind:data-test-role="`filter-${option[filterProps.idProp]}`"
                        v-on:update:model-value="updateFilterSelected(option)" />
                </div>
            </div>
            <div class="mt-2 d-flex flex-row justify-content-end">
                <pc-button
                    class="col-12"
                    v-on:click="updateFilterSelected(null)">
                    {{ resetFiltersText }}
                </pc-button>
            </div>
        </div>
    </div>
</template>
<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/searchFilter.i18n";

    export const FILTER_PROPS = {
        idProp: "id",
        textProp: "name",
        groupIdProp: "category"
    };

    export default {
        directives: {
            "click-outside": {
                beforeMount: (el, binding) => {
                    el.clickOutsideEvent = (event) => {
                        if (!(el == event.target || el.contains(event.target))) {
                            binding.value();
                        }
                    };
                    document.body.addEventListener("click", el.clickOutsideEvent);
                },
                unmounted: (el) => {
                    document.body.removeEventListener("click", el.clickOutsideEvent);
                }
            }
        },
        props: {
            options: { type: Array, required: true },
            initialFilters: { type: Array, required: true },
            noFilterText: { type: String, required: true }
        },
        emits: ["filter-update"],
        data() {
            return {
                filters: this.initialFilters,
                showDropdown: false,
                filterProps: FILTER_PROPS
            };
        },
        computed: {
            filterDisplayText() {
                if (this.filters.length > 0) {
                    return this.filters[0][FILTER_PROPS.textProp];
                }
                return this.noFilterText;
            },
            plusNumber() {
                if (this.filters.length < 2) {
                    return null;
                }
                return `+${this.filters.length - 1}`;
            },
            showPlusNumber() {
                return Boolean(this.plusNumber);
            },
            ungroupedOptions() {
                return this.options.filter((o) => !o[FILTER_PROPS.groupIdProp]);
            },
            groupedOptions() {
                const groupId = FILTER_PROPS.groupIdProp;
                const groups = this.options
                    .filter((o) => o[groupId])
                    .reduce((pv, o) => {
                        (pv[o[groupId]] = pv[o[groupId]] || []).push(o);
                        return pv;
                    }, {});
                const groupArray = [];
                for (const g in groups) {
                    groupArray.push({ name: g, options: groups[g] });
                }
                return groupArray;
            },
            noFilterSelected() {
                return this.filters.length <= 0;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        methods: {
            toggleFloat() {
                this.showDropdown = !this.showDropdown;
            },
            hideFloat() {
                this.showDropdown = false;
            },
            filterIsSelected(filter) {
                if (!filter) {
                    return this.noFilterSelected;
                }
                return this.filters.some((f) => f[FILTER_PROPS.idProp] === filter[FILTER_PROPS.idProp]);
            },
            updateFilterSelected(filter) {
                if (!filter) {
                    // Option for no filter selected
                    // OR all active filters unselected
                    // Remove all filters
                    this.filters = [];
                } else if (this.filterIsSelected(filter)) {
                    // Active filter unselected
                    // Remove caller from filters
                    this.filters.splice(
                        this.filters.map((f) => f[FILTER_PROPS.idProp]).indexOf(filter[FILTER_PROPS.idProp]),
                        1
                    );
                } else if (
                    this.filters.length <= 0 ||
                    this.filters[0][FILTER_PROPS.groupIdProp] !== filter[FILTER_PROPS.groupIdProp]
                ) {
                    // No active filters
                    // OR current active filters are in a different group
                    // Remove all filters except caller
                    this.filters = [filter];
                } else {
                    // Current active filters are in the same group as caller
                    // Add caller to active filters
                    this.filters.push(filter);
                }
                this.$emit("filter-update", this.filters);
            }
        }
    };
</script>
<style lang="scss">
    .float-selector {
        z-index: 1000;
        position: absolute;
        transform: translate(-8.75px, 25px);
    }
    .options-container {
        max-height: 40vh;
        overflow-y: auto;
    }
</style>
